/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 71 lines
 * - Adding: ~6 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCAST_FORM_SPLIT_HOOK_ACTIONS
 * Date:         2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Extracted submit action from BroadcastForm.tsx.
 */

import { useNavigate } from "react-router-dom";
import { createBroadcast, sendBroadcastGroup, BroadcastCreateData } from "../../../../services/broadcastService";
import { notify } from "../../../../utils/notifications";
import { buildTouchedShape, flattenErrors, summarizeByItem } from "../utils/broadcastHelpers";

export const useBroadcastActions = () => {
  const navigate = useNavigate();

  const handleSubmit = async (
    values: BroadcastCreateData,
    action: "draft" | "next",
    helpers: any,
    setIsSubmitting: (val: boolean) => void,
  ) => {
    const { setSubmitting, setTouched, validateForm } = helpers;
    setIsSubmitting(true);
    setSubmitting(true);

    setTouched(buildTouchedShape(values), true);
    const errors = await validateForm();
    const flat = flattenErrors(errors);

    if (Object.keys(flat).length > 0) {
      const lines = summarizeByItem(flat);
      const message = lines.length
        ? `Please review the following required fields:\n${lines.join("\n")}`
        : "Please check the form and fix any errors before continuing";
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(null, message);

      const firstPath = Object.keys(flat)[0];
      if (firstPath) {
        const el = document.querySelector(`[data-field-path="${firstPath}"]`) as HTMLElement | null;
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
        (el as any)?.focus?.();
      }

      setIsSubmitting(false);
      helpers.setSubmitting(false);
      return;
    }

    try {
      const itemCount =
        (Array.isArray((values as any)?.broadcast) && (values as any).broadcast.length) ||
        (Array.isArray((values as any)?.items) && (values as any).items.length) ||
        0;
      console.log("[UI] Submitting broadcast", {
        action,
        type: values?.type,
        itemCount,
      });
      const res = await createBroadcast(values);
      if (action === "draft") {
        notify.success("Your broadcast was saved as a draft");
      } else {
        const broadcastId = (res as any)?.data?.data?._id;
        console.log("[UI] Broadcast created", { broadcastId });

        console.log("[UI] Sending broadcast via API", {
          broadcastId,
          endpoint: "/api/broadcast-groups/:id/send",
        });

        await sendBroadcastGroup(broadcastId);
        console.log("[UI] Send broadcast API call completed", { broadcastId });

        navigate("/broadcast/mine-all", {
          state: { message: "Broadcast sent successfully", broadcastId },
        });
      }
    } catch (error: any) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      if (status === 429 && data?.error === "QUOTA_EXCEEDED") {
        console.warn("[UI] Broadcast quota exceeded", {
          blockedCategories: data?.blockedCategories,
          categories: data?.categories,
          limit: data?.limit,
          resetAtUtc: data?.resetAtUtc,
          message: data?.message,
        });
      }
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(error, "We couldn't send your broadcast. Please try again");
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  return { handleSubmit };
};
